import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { formatDate } from "@/lib/format";
import {
    TrendingUp,
    Package,
    Trophy,
    ArrowUpDown,
    ArrowDown,
    ArrowUp,
    Search,
    X,
    Printer,
    Users,
    MapPin,
    ChevronDown,
} from "lucide-react";

const PERIODS = [
    { id: "today", label: "Vandaag" },
    { id: "this_week", label: "Deze week" },
    { id: "last_7", label: "Laatste 7 dagen" },
    { id: "last_30", label: "Laatste 30 dagen" },
    { id: "ytd", label: "Dit jaar" },
    { id: "all", label: "Alles" },
];

const SORTABLE = ["sku", "name", "pot_size", "order_count", "total_quantity"];

const formatNumber = (n) => new Intl.NumberFormat("nl-NL").format(n ?? 0);

export default function Sales() {
    const [period, setPeriod] = useState("last_30");
    const [customerId, setCustomerId] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [sortKey, setSortKey] = useState("total_quantity");
    const [sortDir, setSortDir] = useState("desc");
    const [customerSearch, setCustomerSearch] = useState("");

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError("");
            setData(null);
            try {
                const params = new URLSearchParams({ period });
                if (customerId) params.set("customer_id", customerId);
                const { data } = await api.get(`/sales/summary?${params}`);
                if (!cancelled) setData(data);
            } catch (e) {
                if (!cancelled)
                    setError(formatApiErrorDetail(e.response?.data?.detail ?? "Kon verkoopdata niet ophalen"));
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [period, customerId]);

    const items = useMemo(() => {
        if (!data?.items) return [];
        const arr = [...data.items];
        arr.sort((a, b) => {
            const av = a[sortKey];
            const bv = b[sortKey];
            if (typeof av === "number" && typeof bv === "number") {
                return sortDir === "asc" ? av - bv : bv - av;
            }
            const as = (av ?? "").toString().toLowerCase();
            const bs = (bv ?? "").toString().toLowerCase();
            if (as < bs) return sortDir === "asc" ? -1 : 1;
            if (as > bs) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return arr;
    }, [data, sortKey, sortDir]);

    const filteredCustomers = useMemo(() => {
        const q = customerSearch.trim().toLowerCase();
        const list = data?.customers || [];
        if (!q) return list;
        return list.filter((c) =>
            [c.name, c.address, c.route_code].join(" ").toLowerCase().includes(q)
        );
    }, [data, customerSearch]);

    const toggleSort = (key) => {
        if (!SORTABLE.includes(key)) return;
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir(key === "total_quantity" || key === "order_count" ? "desc" : "asc");
        }
    };

    const totals = data?.totals || {};
    const top = totals.top_product;
    const selectedCustomer = (data?.customers || []).find((c) => c.customer_id === customerId);

    const subtitle = useMemo(() => {
        if (!data) return "Verkooptotalen per product, gefilterd op periode.";
        const range = data.range || {};
        let base;
        if (data.period === "all") base = "Alle leveringsdatums";
        else if (range.start && range.end) {
            base =
                range.start === range.end
                    ? `Leveringen op ${formatDate(range.start)}`
                    : `${formatDate(range.start)} – ${formatDate(range.end)}`;
        } else base = data.period_label || "—";
        return selectedCustomer
            ? `${base} · gefilterd op ${selectedCustomer.name}`
            : base;
    }, [data, selectedCustomer]);

    return (
        <div className="p-5 sm:p-7 lg:p-10 xl:p-12 max-w-[1480px] mx-auto pb-24 lg:pb-12" data-testid="sales-page">
            <PageHeader
                kicker="Florefy Flow"
                title="Verkoopoverzicht"
                subtitle={subtitle}
                testid="sales-header"
                actions={
                    <>
                        {selectedCustomer && (
                            <>
                                <button
                                    onClick={() => setCustomerId("")}
                                    data-testid="btn-clear-customer"
                                    className="px-3 py-1.5 rounded-md border border-border bg-white hover:bg-ink-50 text-xs flex items-center gap-1.5"
                                >
                                    <X className="h-3.5 w-3.5" /> Klantfilter wissen
                                </button>
                                <button
                                    onClick={() => window.print()}
                                    data-testid="btn-print-invoice"
                                    className="px-4 py-2 rounded-md bg-florefy-900 hover:bg-florefy-700 text-white text-sm flex items-center gap-2"
                                >
                                    <Printer className="h-4 w-4" /> Factuur afdrukken
                                </button>
                            </>
                        )}
                    </>
                }
            />

            {/* Filter bar — periode + klant in 1 rij */}
            <div
                className="bg-white border border-border rounded-md p-3 mb-6 flex flex-wrap items-center gap-2 print:hidden"
                data-testid="sales-filterbar"
            >
                <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Periode">
                    {PERIODS.map((p) => {
                        const active = period === p.id;
                        return (
                            <button
                                key={p.id}
                                onClick={() => setPeriod(p.id)}
                                data-testid={`period-${p.id}`}
                                data-active={active}
                                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                                    active
                                        ? "bg-florefy-900 text-white border-florefy-900"
                                        : "bg-white text-ink-700 border-border hover:bg-ink-50"
                                }`}
                            >
                                {p.label}
                            </button>
                        );
                    })}
                </div>
                <div className="h-6 w-px bg-border mx-2" />
                <div className="relative inline-flex items-center" data-testid="customer-filter-wrap">
                    <Users className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                    <select
                        value={customerId}
                        onChange={(e) => setCustomerId(e.target.value)}
                        data-testid="customer-filter"
                        className="appearance-none pl-7 pr-7 py-1.5 text-xs font-medium border border-border rounded-md bg-white text-ink-700 hover:bg-ink-50 focus:outline-none focus:border-florefy-700 cursor-pointer transition-colors max-w-[220px] truncate"
                    >
                        <option value="">Alle klanten ({(data?.customers || []).length})</option>
                        {(data?.customers || []).map((c) => (
                            <option key={c.customer_id} value={c.customer_id}>
                                {c.name} ({formatNumber(c.total_quantity)} stuks)
                            </option>
                        ))}
                    </select>
                    <ChevronDown className="h-3.5 w-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                    {customerId && (
                        <button
                            onClick={() => setCustomerId("")}
                            data-testid="clear-customer-filter"
                            className="ml-2 text-xs text-ink-500 hover:text-florefy-900 inline-flex items-center gap-1"
                            title="Filter wissen"
                        >
                            <X className="h-3 w-3" /> Wissen
                        </button>
                    )}
                </div>
            </div>

            {/* Klantdetail-card (alleen wanneer een klant gefilterd is — voor factuur) */}
            {selectedCustomer && (
                <div
                    className="bg-white border border-florefy-200 rounded-md p-5 mb-6"
                    data-testid="selected-customer-card"
                >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <div className="data-label mb-1">Geselecteerde klant</div>
                            <h2 className="text-xl font-semibold text-ink-900" data-testid="selected-customer-name">
                                {selectedCustomer.name}
                            </h2>
                            {selectedCustomer.address && (
                                <div className="text-sm text-ink-600 mt-1 flex items-center gap-1.5">
                                    <MapPin className="h-3.5 w-3.5 text-ink-400" />
                                    {selectedCustomer.address}
                                </div>
                            )}
                            {selectedCustomer.route_code && (
                                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-500 mt-1">
                                    Route {selectedCustomer.route_code}
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-3 gap-x-6 text-right">
                            <div>
                                <div className="data-label">Bestellingen</div>
                                <div className="text-2xl font-mono font-semibold text-ink-900 mt-0.5">
                                    {formatNumber(selectedCustomer.order_count)}
                                </div>
                            </div>
                            <div>
                                <div className="data-label">Stuks</div>
                                <div className="text-2xl font-mono font-semibold text-ink-900 mt-0.5">
                                    {formatNumber(selectedCustomer.total_quantity)}
                                </div>
                            </div>
                            <div>
                                <div className="data-label">Soorten</div>
                                <div className="text-2xl font-mono font-semibold text-ink-900 mt-0.5">
                                    {items.length}
                                </div>
                            </div>
                        </div>
                    </div>
                    {selectedCustomer.delivery_dates && selectedCustomer.delivery_dates.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-border/60">
                            <div className="data-label mb-2">Leveringen in deze periode</div>
                            <div className="flex flex-wrap gap-1.5">
                                {selectedCustomer.delivery_dates.map((d) => (
                                    <span
                                        key={d}
                                        className="font-mono text-[11px] px-2 py-0.5 rounded-sm bg-ink-100 text-ink-700"
                                    >
                                        {formatDate(d)}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Stat tiles — compacter, niet meer 4xl mono */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                <StatTile
                    label="Verkochte stuks"
                    value={loading ? "…" : formatNumber(totals.total_units)}
                    sub={`${formatNumber(totals.total_orders)} bestelling${totals.total_orders === 1 ? "" : "en"}`}
                    icon={TrendingUp}
                    accent
                    testid="stat-total-units"
                />
                <StatTile
                    label="Soorten"
                    value={loading ? "…" : formatNumber(totals.total_products)}
                    sub={
                        totals.total_products
                            ? "unieke producten"
                            : "geen verkoop"
                    }
                    icon={Package}
                    testid="stat-total-products"
                />
                <StatTile
                    label="Klanten"
                    value={loading ? "…" : formatNumber(totals.total_customers)}
                    sub="met leveringen in periode"
                    icon={Users}
                    testid="stat-total-customers"
                />
                <StatTile
                    label="Bestverkocht"
                    value={loading ? "…" : top ? top.name : "—"}
                    sub={top ? `${formatNumber(top.quantity)} stuks · ${top.sku}` : "—"}
                    icon={Trophy}
                    compact
                    testid="stat-top-product"
                />
            </div>

            {/* Klanten-tabel (alleen tonen als geen klant gefilterd is) */}
            {!selectedCustomer && (data?.customers || []).length > 0 && (
                <div className="bg-white border border-border rounded-md mb-6 overflow-hidden" data-testid="customers-section">
                    <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
                        <h2 className="text-base font-semibold text-ink-900 flex items-center gap-2">
                            <Users className="h-4 w-4" /> Klanten in deze periode
                        </h2>
                        <div className="relative">
                            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
                            <input
                                type="search"
                                value={customerSearch}
                                onChange={(e) => setCustomerSearch(e.target.value)}
                                placeholder="Zoek klant…"
                                data-testid="customer-search"
                                className="pl-7 pr-2 py-1 text-xs border border-border rounded-md w-48 focus:outline-none focus:border-florefy-500"
                            />
                        </div>
                    </div>
                    <table className="w-full text-sm" data-testid="customers-table">
                        <thead>
                            <tr className="bg-ink-50">
                                <th className="text-left px-5 py-2 data-label">Klant</th>
                                <th className="text-left px-5 py-2 data-label">Route</th>
                                <th className="text-right px-5 py-2 data-label">Bestellingen</th>
                                <th className="text-right px-5 py-2 data-label">Stuks</th>
                                <th className="w-10 px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCustomers.map((c) => (
                                <tr
                                    key={c.customer_id}
                                    className="border-t border-border/60 hover:bg-florefy-50/40 cursor-pointer transition-colors"
                                    onClick={() => setCustomerId(c.customer_id)}
                                    data-testid={`customer-row-${c.customer_id}`}
                                >
                                    <td className="px-5 py-2.5">
                                        <div className="font-medium text-ink-900">{c.name}</div>
                                        {c.address && (
                                            <div className="text-xs text-ink-500">{c.address}</div>
                                        )}
                                    </td>
                                    <td className="px-5 py-2.5 font-mono text-xs text-ink-700">
                                        {c.route_code || "—"}
                                    </td>
                                    <td className="px-5 py-2.5 font-mono text-right text-ink-700">
                                        {formatNumber(c.order_count)}
                                    </td>
                                    <td className="px-5 py-2.5 font-mono font-semibold text-right text-ink-900">
                                        {formatNumber(c.total_quantity)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right text-ink-400 text-xs">
                                        →
                                    </td>
                                </tr>
                            ))}
                            {filteredCustomers.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-5 py-6 text-center text-ink-500 text-sm">
                                        Geen klanten gevonden.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Verkoop per soort */}
            <div className="bg-white border border-border rounded-md overflow-hidden" data-testid="sales-table-wrapper">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <h2 className="text-base font-semibold text-ink-900">
                        {selectedCustomer ? "Factuurregels — Verkoop per soort" : "Verkoop per soort"}
                    </h2>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-ink-500">
                        {data?.period_label || "—"}
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="sales-table">
                        <thead>
                            <tr className="bg-ink-50">
                                <Th label="SKU" sortKey="sku" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                                <Th label="Naam" sortKey="name" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                                <Th label="Pot" sortKey="pot_size" activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                                <Th label="Bestellingen" sortKey="order_count" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                                <Th label="Aantal" sortKey="total_quantity" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                            </tr>
                        </thead>
                        <tbody>
                            {error && (
                                <tr>
                                    <td colSpan={5} className="px-5 py-6 text-center text-red-700 text-sm" data-testid="sales-error">
                                        {error}
                                    </td>
                                </tr>
                            )}
                            {!error && !loading && items.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-5 py-12 text-center text-ink-500" data-testid="sales-empty">
                                        Geen verkopen in deze periode.
                                    </td>
                                </tr>
                            )}
                            {!error &&
                                items.map((it) => (
                                    <tr
                                        key={it.product_id}
                                        className="border-t border-border/60 hover:bg-ink-50/40 transition-colors"
                                        data-testid={`sales-row-${it.sku}`}
                                    >
                                        <td className="px-5 py-2.5 font-mono text-xs text-ink-700">{it.sku}</td>
                                        <td className="px-5 py-2.5 text-ink-900">
                                            <div className="font-medium">{it.name}</div>
                                            {it.cultivar && (
                                                <div className="text-xs text-ink-500 italic">{it.cultivar}</div>
                                            )}
                                        </td>
                                        <td className="px-5 py-2.5 font-mono text-xs text-ink-700">
                                            {it.pot_size || "—"}
                                            {it.pot_liters && <span className="text-ink-500"> · {it.pot_liters}</span>}
                                        </td>
                                        <td className="px-5 py-2.5 font-mono text-ink-700 text-right">
                                            {formatNumber(it.order_count)}
                                        </td>
                                        <td className="px-5 py-2.5 font-mono font-semibold text-ink-900 text-right">
                                            {formatNumber(it.total_quantity)}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                        {!error && items.length > 0 && (
                            <tfoot>
                                <tr className="bg-ink-50 border-t border-border">
                                    <td colSpan={3} className="px-5 py-3 font-mono text-[10px] uppercase tracking-widest text-ink-500">
                                        Totaal · {data?.period_label}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-ink-700 text-right">
                                        {formatNumber(totals.total_orders)}
                                    </td>
                                    <td className="px-5 py-3 font-mono font-semibold text-ink-900 text-right" data-testid="sales-grand-total">
                                        {formatNumber(totals.total_units)}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
}

function StatTile({ icon: Icon, label, value, sub, accent, compact, testid }) {
    return (
        <div
            className={`border rounded-md p-3 ${accent ? "bg-florefy-50 border-florefy-200" : "bg-white border-border"}`}
            data-testid={testid}
        >
            <div className="flex items-center justify-between mb-1">
                <div className="data-label leading-tight">{label}</div>
                {Icon && <Icon className={`h-3.5 w-3.5 ${accent ? "text-florefy-700" : "text-ink-400"}`} />}
            </div>
            <div className={`font-mono font-semibold leading-tight ${compact ? "text-base text-ink-900" : (accent ? "text-3xl text-florefy-900" : "text-2xl text-ink-900")}`}>
                {value}
            </div>
            {sub && (
                <div className="text-[11px] text-ink-500 mt-1 leading-tight truncate" title={sub}>
                    {sub}
                </div>
            )}
        </div>
    );
}

function Th({ label, sortKey, activeKey, dir, onClick, align = "left" }) {
    const active = activeKey === sortKey;
    const SortIcon = !active
        ? ArrowUpDown
        : dir === "asc"
        ? ArrowUp
        : ArrowDown;
    return (
        <th
            scope="col"
            onClick={() => onClick(sortKey)}
            className={`px-5 py-2.5 data-label cursor-pointer select-none whitespace-nowrap ${
                align === "right" ? "text-right" : "text-left"
            } ${active ? "text-ink-900" : ""}`}
            data-testid={`sales-th-${sortKey}`}
            data-sort-active={active}
            data-sort-dir={active ? dir : undefined}
        >
            {label}
            <SortIcon className={`inline h-3 w-3 ml-1 ${active ? "" : "opacity-40"}`} />
        </th>
    );
}
